import Footer from '../components/Footer'
import ArrowUp from '../Icons/faq/arrow-up.svg'
import ArrowDown from '../Icons/faq/arrow-down.svg'
import { useEffect, useState } from 'react'
import ShallowHeader from '../components/ShallowHeader'
import SectionHeading from '../components/HomePage/SectionHeading'
import { removeSpaceFromString } from '../helpers/utils'
import { Combobox } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import HeroBanner from '../components/FAQ/HeroBanner'
import CallAction from '../components/CallActions/CallActionTypeOne'
import BookACallButton from '../components/Buttons/Specials/BookACallButton'

const ButtonForCallAction = () => {
    return <BookACallButton>Book A Call!</BookACallButton>
}

const faqData = [
    {
        category: 'Lists',
        questions: [
            {
                question: 'What are the best lists to mail to?',
                answer: `There are two types of lists: General and Niche. General lists are easy to pull and can be done from many list building software services. Niche lists take more time to pull as they are not readily available and it will take more time to pull. However, Niche lists are superior to General lists because of the difficulty in pulling nature and because they general indicate higher motivation to sell. Here is a list of General and Niche lists that we recommend, along with how to pull each general list.
                <br/>
                <a target="_blank"
                class='inline-block hover:bg-olh hover:text-white my-4 px-2 border-2 border-olh rounded-lg text-olh'
                href="https://drive.google.com/file/d/1BlrLP8BK5QDPJIc3ni7PJDchj8IEBEeI/view?pli=1"
                >Recommended Prospect Lists</a>
                <br/>
                <a target="_blank"
                class='inline-block hover:bg-olh hover:text-white mb-4 px-2 border-2 border-olh rounded-lg text-olh'
                href="https://drive.google.com/file/d/1m8ljiiJjMWstBcBM6J3m5P5osEfcdvyC/view" >General List Criteria</a>
                `,
            },
            {
                question: 'How do I need to format my list?',
                answer: `Lists must be in .csv format. You can also use our Lead List Template which can be found <a
                href="https://admin.openlettermarketing.com/wp-content/uploads/2023/11/OLM-Lead-Template.csv"
                class='hover:text-olh text-olm'
                >here</a>.`,
            },
            {
                question:
                    'Do you provide mailing lists, or do I need to supply my own?',
                answer: 'We can absolutely work with you to pull your list or you can supply your own. If you’re interested in us pulling a list for you, please call us at <a class="text-olm hover:text-olh" href="tel:978-269-0245">978-269-0245</a> or <button class="text-olm hover:text-olh" onClick={window.HubSpotConversations.widget.open()}>chat with us!</button>',
            },
        ],
    },
    {
        category: 'Direct Mail Best Practices',
        questions: [
            {
                question: 'How many times should I mail to a list?',
                answer: 'We recommend 6 touches for optimal results. Give us a call or select one of our Proven and Tested Campaigns here! <a href="https://openlettermarketing.com/product/proven-mix-campaigns" className="text-olm hover:text-olh">Proven Mix Campaigns.</a>',
            },
            {
                question: 'What’s better, postcards or letters?',
                answer: 'We recommend mixing and matching all of our products to see what works best in your farm area. Postcards are cost-effective, while letters offer a personal touch and contain more information. It really depends on your Sales Strategy and who you are targeting!',
            },
            {
                question: 'What is the ROI for your direct mail campaigns?',
                answer: 'ROI varies by list quality and frequency, but consistent campaigns typically see the highest returns.',
            },
            {
                question: 'How should I handle undeliverable mail?',
                answer: 'Undeliverable mail can be a goldmine! The USPS \'nixie label\' provides the reason for the return. Use that information to skip trace the contact, update their address, and start mailing to them again for potential deals. Visit our <a href="https://openlettermarketing.com/service/skip-trace-phone-email-append" class="text-olm hover:text-olh">skip trace</a> page.',
            },
        ],
    },
    {
        category: 'Real Estate Questions',
        questions: [
            {
                question: 'How do I get probate records?',
                answer: 'We recommend that you use <a href="http://successorsdata.com/" target="_blank" class="text-olm hover:text-olh">successorsdata.com</a> to pull probate leads.',
            },
            {
                question: 'How do I find motivated sellers?',
                answer: 'Identify a motivating reason why someone would want to sell their home. Focus on foreclosure, late property taxes, or other issues. Then, pull lists matching those criteria.',
            },
            {
                question:
                    'What is a distressed property and why should I target them?',
                answer: 'A distressed property is in poor condition or facing financial difficulties. These are often sold below Market Value, offering great investment opportunities.',
            },
            {
                question: 'Why is skip tracing important?',
                answer: 'Skip tracing finds updated contact information for leads. This maximizes the chances of connecting with potential sellers who might otherwise be unreachable. Visit our skip trace page <a href="https://openlettermarketing.com/service/skip-trace-phone-email-append" class="text-olm hover:text-olh">here</a>!',
            },
            {
                question:
                    'How do I determine the after-repair value (ARV) of a property?',
                answer: 'ARV is the estimated sale price after repairs. Research similar sold properties in the area (comping) using tools like Zillow or Redfin to calculate the ARV.',
            },
            {
                question: 'What is a FOIA?',
                answer: 'A FOIA request accesses public records from government agencies. Use it to obtain data like code violations, tax liens, and eviction records.',
            },
        ],
    },
    {
        category: 'OLM Procedures',
        questions: [
            {
                question: 'What days do you mail?',
                answer: 'We mail on Monday, Wednesday, and Friday.',
            },
            {
                question:
                    'Can I customize the messaging and design of my mail pieces?',
                answer: 'Yes! All products are fully customizable; add logos, QR codes, and more.',
            },
            {
                question:
                    'How do you handle changes or adjustments during a campaign?',
                answer: 'We share a Google Sheet of your Mailing List during the proofing process, allowing you to edit/add/subtract records.',
            },
            {
                question:
                    'Do you remove undeliverable contacts / leads upon list upload?',
                answer: 'Yes! We scrub addresses against a USPS database to remove undeliverable mail.',
            },
            {
                question: 'What is the $19 undeliverable charge?',
                answer: 'The $19 charge provides a list of undeliverables scrubbed from your uploaded lists.',
            },
            {
                question: 'Can I split my mailing list into multiple batches?',
                answer: 'Yes! Batching is part of our services.',
            },
            {
                question:
                    'Can I schedule my mailings to go out on specific dates?',
                answer: 'Yes! You can select specific mail dates during proof approval or discuss with your Marketing Coach.',
            },
            {
                question:
                    'Do you offer samples or can I get a sample of my mail piece before it goes out?',
                answer: `If you are interested in physical samples of OLM’s Direct Mail Products, you can fill out
                <a href="https://openlettermarketing.com/get-free-samples"
                class="text-olm hover:text-olh"
                >this form </a>to have samples shipped directly to you!. If you’d like a sample of your mail piece, then we will send you a PDF proof. It is 100% part of our process for you to receive, review, and approve your proof via PDF doc before anything ships.`,
            },
            {
                question:
                    'How do I update my mailing list after submitting an order?',
                answer: 'Contact your Marketing Coach promptly if you need to update your mailing list before production.',
            },
            {
                question: 'What is NCOA? (National Change of Address)',
                answer: 'The NCOA is a USPS database that tracks address changes to reduce undeliverable mail.',
            },
            {
                question:
                    'There is an address that is not on my mailing list. Why did it change?',
                answer: 'This happens due to NCOA, which matches mailing addresses to the most up-to-date address.',
            },
            {
                question: 'What is a proof and why is it important?',
                answer: 'A proof is a PDF mockup of your order. It ensures your order is accurate and meets your expectations.',
            },
            {
                question: 'How many lists can I upload for an order?',
                answer: 'You can upload as many lists as needed during the list upload stage.',
            },
            {
                question: 'How many tracking numbers can one order have?',
                answer: "There is no limit. Add a 'retphone' column to your list for assigning phone numbers.",
            },
        ],
    },
    {
        category: 'Ordering and Pricing',
        questions: [
            {
                question: 'How do I place an order?',
                answer: '<a href="https://openlettermarketing.com/coaching" class="text-olm hover:text-olh">Book a call with a Marketing Coach</a> or place orders directly on our site.',
            },
            {
                question: 'Do I place my order myself?',
                answer: 'You can place orders online or work with a Marketing Coach at 978-269-0245.',
            },
            {
                question: 'Can I use my own design?',
                answer: 'Yes, but there may be a $65 fee if modifications are needed to ensure compatibility.',
            },
            {
                question: 'What is included in your pricing?',
                answer: 'Pricing includes postage, materials, labor, and more—no hidden fees!',
            },
            {
                question: 'What is the minimum commitment required?',
                answer: 'Letters require 200 pieces; postcards require 500 pieces. Call for more specifics.',
            },
            {
                question: 'Do you offer volume discounts?',
                answer: 'Yes, schedule a call with a Marketing Coach to discuss.',
            },
            {
                question: 'Do you price match?',
                answer: 'Yes! Contact us at 978-269-0245 for details.',
            },
        ],
    },
    {
        category: 'OLM Service Offerings',
        questions: [
            {
                question: 'Can you create my design for me?',
                answer: 'Absolutely! We have several talented Graphic Designers who are able to work with you on design. We charge a one-time design fee of $65 per custom template.',
            },
            {
                question:
                    'How does the ROS offer system work? Can I use my own offers?',
                answer: '“ROS” stands for Rapid Offer System. Our ROS products run your property addresses through our proprietary software to generate an estimated market value. You can then choose a percentage of that value to use as the cash offer on each mailer. You ABSOLUTELY can use your own offers instead of ours!',
            },
            {
                question: 'What does ROS stand for?',
                answer: '“ROS” stands for Rapid Offer System. Our ROS products run your property addresses through our proprietary software to generate an estimated market value. You can then choose a percentage of that value to use as the cash offer on each mailer.',
            },
            {
                question: 'What is Skip Tracing?',
                answer: `Skip tracing is the process of finding updated contact information for leads who are hard to reach due to outdated addresses or phone numbers. It works by using data sources to search for the most recent details on an individual, such as new phone numbers, email addresses, or current addresses. This helps real estate investors connect with potential sellers who might otherwise be unreachable, maximizing the chances of closing a deal. You can skip trace on our website <a href="https://openlettermarketing.com/service/skip-trace-phone-email-append"
                class="text-olm hover:text-olh"
                >here!</a>`,
            },
            {
                question:
                    'What makes OLM unique compared to other mail houses?',
                answer: 'We work hands on with thousands of investors and collect performance metrics on the strategies and mailers that drive performance for them. We pass along that knowledge to you to get you the best results possible for your marketing spend. OLM is quick and efficient, with proofs within 24hrs and orders sent within 5-7 days of payment. Each client gets a dedicated marketing coach, competitive pricing, and top-notch support.',
            },
            {
                question:
                    'Can I personalize each mail piece with different variable information?',
                answer: 'Absolutely! There really is no limit to the amount of variable data you can add to your mail piece with OLM.',
            },
            {
                question: 'Do you insert business cards into envelopes?',
                answer: 'We do not offer that service at this time.',
            },
            {
                question: 'Do you remove duplicates?',
                answer: 'Yes! During order placement, you will have the ability to select if you’d like Mailing and or Property Address Duplicates removed.',
            },
            {
                question:
                    'Can I add a QR code or unique tracking link to my mail piece?',
                answer: 'Yes! You can either supply your own QR code or request our Prepress department to create one for you.',
            },
            {
                question: 'Why is there a $65 fee for unsealed letters?',
                answer: 'Our Real Penned Letters (Personal and Professional) can be shipped to you unsealed upon request. This service incurs an additional charge because our machinery is set up by default to seal letters with liquid glue. Disabling this feature requires a manual adjustment, which increases the chance of errors and adds complexity to our process.',
            },
            {
                question:
                    'Why are you shipping my Real Penned Letters directly to me in a USPS Priority Mail Box?',
                answer: "Real Penned Letters are our most authentic direct mail piece, featuring a Forever Stamp instead of a bulk mail stamp. Since Forever Stamps must be 'canceled' with a postmark at a USPS location, we ship them directly to you to maintain authenticity.",
            },
            {
                question: 'Do you allow people to purchase store credit?',
                answer: `Yes we do. You can purchase store credit <a href="https://openlettermarketing.com/my-account/orders"
                class="text-olm hover:text-olh"
                >here!</a>`,
            },
        ],
    },
    {
        category: 'Delivery and Tracking',
        questions: [
            {
                question:
                    'What is the difference between First-Class and Standard Class Mail?',
                answer: 'First-Class Mail typically takes USPS 1-5 business days to deliver, as opposed to Standard Class mail which is 7-21 days. First-Class mail also returns undeliverable mail while Standard Class does not.',
            },
            {
                question: 'How long does it take for my mail to be delivered?',
                answer: 'It depends on the type of postage. First-Class Mail delivers in 1-5 business days, while Standard Class Mail typically takes 7-21 business days.',
            },
            {
                question:
                    'What is the turnaround time for mailing once my order is placed?',
                answer: 'Once your order is placed, you will receive a proof within 24 hours. After approval, you can select the next available mail date, usually within 3 days.',
            },
            {
                question: 'Do you offer international mailing services?',
                answer: 'OLM only mails within the United States.',
            },
        ],
    },
    {
        category: 'Support and Assistance',
        questions: [
            {
                question:
                    'What type of support do you provide during the campaign?',
                answer: 'Before, during, and even after your campaign, we are available to answer any questions. We guide you through the process, check in periodically, and gather feedback for future campaign improvements.',
            },
        ],
    },
]

export default function FAQs() {
    const [activeHash, setActiveHash] = useState('')
    const [query, setQuery] = useState('')
    const [activeIndex, setActiveIndex] = useState(null)
    const [filteredQuestions, setFilteredQuestions] = useState([])

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index)
    }

    useEffect(() => {
        const hash = window.location.hash
        setActiveHash(hash)
    }, [])

    // Filter questions based on query
    useEffect(() => {
        if (query) {
            const results = faqData.flatMap((faq) =>
                faq.questions
                    .filter((q) =>
                        q.question.toLowerCase().includes(query.toLowerCase()),
                    )
                    .map((question) => ({
                        question: question.question,
                        category: faq.category,
                    })),
            )
            setFilteredQuestions(results)
        } else {
            setFilteredQuestions([])
        }
    }, [query])

    return (
        <div>
            <ShallowHeader title={"FAQ's"} />
            <div className="bg-light-yellow-gradient-transparent -mt-40 pt-40 pb-0">
                <HeroBanner />
            </div>
            <div className="bg-new-yellow py-4 flex justify-center">
                <div className="max-w-5xl w-full flex-grow bg-white rounded-lg shadow-md mx-4 md:mx-0">
                    <Combobox value={query} onChange={setQuery}>
                        <div className="relative w-full">
                            <Combobox.Input
                                className="pl-4 p-2 text-sm w-full h-12 md:h-16 text-gray-700 rounded-lg bg-transparent shadow-md border-0 border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                placeholder="Search FAQ's"
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <span className="absolute inset-y-0 right-4 flex items-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 md:h-8 md:w-8 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M21 21l-4.35-4.35m2.7-7.8a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
                                    />
                                </svg>
                            </span>

                            {query && (
                                <span className="absolute inset-y-0 right-12 flex items-center">
                                    <XIcon
                                        className="h-6 w-6 md:h-8 md:w-8 text-gray-400 cursor-pointer"
                                        onClick={() => setQuery('')}
                                    />
                                </span>
                            )}

                            {query && filteredQuestions.length > 0 && (
                                <Combobox.Options className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-md w-full">
                                    {filteredQuestions.map((item, idx) => (
                                        <Combobox.Option
                                            key={idx}
                                            value={item.question}
                                            className="cursor-pointer text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() => {
                                                setQuery('')
                                                setActiveHash(
                                                    `#${removeSpaceFromString(
                                                        item.category,
                                                    )}`,
                                                )
                                                document
                                                    .getElementById(
                                                        removeSpaceFromString(
                                                            item.category,
                                                        ),
                                                    )
                                                    .scrollIntoView({
                                                        behavior: 'smooth',
                                                    })
                                                toggleAccordion(
                                                    removeSpaceFromString(
                                                        item.question,
                                                    ),
                                                )
                                            }}>
                                            {item.question}
                                        </Combobox.Option>
                                    ))}
                                </Combobox.Options>
                            )}
                        </div>
                    </Combobox>
                </div>
            </div>

            <div className="bg-light-yellow-gradient py-10 px-4 md:px-0">
                <div className="container m-auto flex items-start md:space-x-10">
                    <div className="w-3/12 space-y-4 my-10 hidden md:block py-3 px-4 bg-white shadow-xl rounded-xl sticky top-10 overflow-y-auto">
                        {faqData.map((faq, idx) => {
                            const category = faq.category
                            const itemHash = `#${removeSpaceFromString(
                                category,
                            )}`
                            return (
                                <a
                                    key={idx}
                                    href={itemHash}
                                    onClick={() => setActiveHash(itemHash)}
                                    className={`p-2 mx-2 my-1 flex px-4 space-x-2 font-bold
                                        ${
                                            activeHash === itemHash
                                                ? 'olm-gradient text-white'
                                                : 'hover:olm-gradient text-black hover:text-white'
                                        }
                                        rounded-full items-center`}>
                                    <span>{category}</span>
                                </a>
                            )
                        })}
                    </div>

                    <div className="md:w-9/12 w-full">
                        {faqData.map((faq, idx) => (
                            <div
                                id={removeSpaceFromString(faq.category)}
                                key={idx}>
                                <div className="space-y-10 py-20">
                                    <h3 className="text-3xl text-left items-center font-semibold md:flex flex-row justify-left space-x-4">
                                        <div className="text-center space-y-4 md:space-x-4 items-center justify-center md:justify-left md:py-2">
                                            <SectionHeading>
                                                <span className="olm-gradient-text">
                                                    {faq.category}
                                                </span>
                                            </SectionHeading>
                                        </div>
                                    </h3>

                                    <div className="w-full">
                                        {faq.questions.map((question, idx) => {
                                            const questionIndex = `${removeSpaceFromString(
                                                question.question,
                                            )}`
                                            return (
                                                <div
                                                    key={idx}
                                                    className="py-6 border-b border-olm">
                                                    <button
                                                        className="w-full text-left flex space-x-4 md:space-x-0 justify-between items-center font-semibold focus:outline-none"
                                                        onClick={() =>
                                                            toggleAccordion(
                                                                questionIndex,
                                                            )
                                                        }>
                                                        <h3 className="md:text-2.5xl text-2xl md:flex-grow w-10/12 md:w-auto">
                                                            {question.question}
                                                        </h3>
                                                        {activeIndex ===
                                                        questionIndex ? (
                                                            <ArrowUp className="h-10 w-10" />
                                                        ) : (
                                                            <ArrowDown className="h-10 w-10" />
                                                        )}
                                                    </button>
                                                    {activeIndex ===
                                                        questionIndex && (
                                                        <div className="mt-2">
                                                            <p
                                                                dangerouslySetInnerHTML={{
                                                                    __html: question.answer,
                                                                }}
                                                                className="text-xl leading-10"></p>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="py-10">
                <CallAction CallActionButton={ButtonForCallAction}>
                    <h2 className="text-4xl lg:text-5xl leading-4 font-bold mb-4 space-x-1">
                        <span>
                            Didn’t find what you were <br /> looking for?
                        </span>
                        <span className="olm-gradient-text">
                            Reach out to us!
                        </span>
                    </h2>
                </CallAction>
            </div>

            <div className="bg-new-yellow py-6">
                <div className="container max-w-5xl mx-auto flex flex-wrap justify-center">
                    {faqData.map((faq, idx) => (
                        <a
                            key={idx}
                            href={`#${removeSpaceFromString(faq.category)}`}
                            className="px-4 py-2 m-2 flex items-center bg-white/50 border-olm border text-olm font-bold rounded-5xl shadow-md  hover:shadow-lg transition duration-200">
                            <span>{faq.category}</span>
                        </a>
                    ))}
                </div>
            </div>

            <Footer />
        </div>
    )
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export function getStaticProps() {
    // const page = await GeneralAPI.getPageBySlug('/about')

    return {
        props: {
            data: {},
        },
        revalidate: 10, // In seconds
    }
}
